import React, { useState, useEffect, useRef } from "react";
import "./MobileTabNav.css";

/**
 * MobileTabNav - A responsive mobile tab navigation component for bottom screen navigation
 *
 * @param {Object} props
 * @param {Array} props.tabs - Array of tab objects with id, label, icon properties
 * @param {string} props.activeTab - Currently active tab ID
 * @param {Function} props.onTabChange - Function to call when tab is changed
 * @param {string} [props.defaultActiveTab] - Optional default active tab ID (if not provided, uses activeTab)
 * @param {Object} [props.style] - Optional custom styles for the container
 * @param {Object} [props.tabStyle] - Optional custom styles for individual tabs
 * @param {Object} [props.activeTabStyle] - Optional custom styles for the active tab
 * @param {string} [props.activeColor] - Optional color for active tab (default: #4a89dc)
 * @param {Boolean} [props.showOnMobileOnly=true] - Whether to show the navigation only on mobile screens
 * @param {Boolean} [props.showLabels=true] - Whether to show tab labels or just icons
 * @param {string} [props.position="bottom"] - Position of the navigation bar ("bottom" or "top")
 * @param {string} [props.ariaLabel="Mobile navigation"] - Aria label for the navigation component
 * @returns {JSX.Element}
 */
const MobileTabNav = ({
  tabs,
  activeTab,
  onTabChange,
  defaultActiveTab,
  style = {},
  tabStyle = {},
  activeTabStyle = {},
  activeColor,
  showOnMobileOnly = true,
  showLabels = true,
  position = "bottom",
  ariaLabel = "Mobile navigation",
}) => {
  // Internal state for managing the active tab
  const [currentActiveTab, setCurrentActiveTab] = useState(
    activeTab || defaultActiveTab || ""
  );

  // Refs for keyboard navigation
  const navRef = useRef(null);
  const buttonRefs = useRef([]);

  // Update internal state when the activeTab prop changes
  useEffect(() => {
    if (activeTab && activeTab !== currentActiveTab) {
      setCurrentActiveTab(activeTab);
    }
  }, [activeTab]);

  // Reset button refs array when tabs change
  useEffect(() => {
    buttonRefs.current = buttonRefs.current.slice(0, tabs.length);
  }, [tabs]);

  // Handle tab click
  const handleTabClick = (tabId) => {
    setCurrentActiveTab(tabId);
    if (onTabChange && typeof onTabChange === "function") {
      onTabChange(tabId);
    }
  };

  // Handle keyboard navigation
  const handleKeyDown = (e, index) => {
    let newIndex;

    switch (e.key) {
      case "ArrowRight":
        e.preventDefault();
        newIndex = (index + 1) % tabs.length;
        buttonRefs.current[newIndex].focus();
        break;
      case "ArrowLeft":
        e.preventDefault();
        newIndex = (index - 1 + tabs.length) % tabs.length;
        buttonRefs.current[newIndex].focus();
        break;
      case "Home":
        e.preventDefault();
        buttonRefs.current[0].focus();
        break;
      case "End":
        e.preventDefault();
        buttonRefs.current[tabs.length - 1].focus();
        break;
      default:
        break;
    }
  };

  // Check if required props are provided
  if (!tabs || !Array.isArray(tabs) || tabs.length === 0) {
    console.warn(
      "MobileTabNav: tabs prop is required and must be a non-empty array"
    );
    return null;
  }

  // Additional classes to apply based on props
  const containerClasses = [
    "mobile-tab-navigation",
    showOnMobileOnly ? "mobile-only" : "",
    position === "top" ? "position-top" : "position-bottom",
  ]
    .filter(Boolean)
    .join(" ");

  // Custom active tab styles including color
  const customActiveStyle = {
    ...activeTabStyle,
    ...(activeColor ? { "--active-color": activeColor } : {}),
  };

  return (
    <div
      className={containerClasses}
      style={style}
      ref={navRef}
      role="tablist"
      aria-label={ariaLabel}
    >
      {tabs.map((tab, index) => {
        const isActive = currentActiveTab === tab.id;
        const buttonStyles = isActive
          ? { ...tabStyle, ...customActiveStyle }
          : tabStyle;

        return (
          <button
            key={tab.id}
            className={isActive ? "active" : ""}
            onClick={() => handleTabClick(tab.id)}
            style={buttonStyles}
            aria-selected={isActive}
            aria-controls={`tab-content-${tab.id}`}
            id={`tab-button-${tab.id}`}
            tabIndex={isActive ? 0 : -1}
            role="tab"
            ref={(el) => (buttonRefs.current[index] = el)}
            onKeyDown={(e) => handleKeyDown(e, index)}
          >
            <span className="tab-icon">{tab.icon}</span>
            {showLabels && <span className="tab-label">{tab.label}</span>}
          </button>
        );
      })}
    </div>
  );
};

export default MobileTabNav;
